/**
 * Comm (커뮤니티) 라우트. exclusive create-post: Quill, 탭, 썸네일/미디어 업로드, 폼 제출.
 * Language 페이지: Country select (signup과 동일), Language select.
 * Profile 페이지: 회원 탈퇴 폼 (Form.ts + confirm).
 */
import { Logger } from "../../utils/Logger";
import { Form } from "../../core/Form";
import { Modal } from "../../utils/Modal";
import { QuillEditor, stripBase64Images } from "../../components/QuillEditor";
import { CountrySelect } from "../components/CountrySelect";
import { CommSortSelect } from "../components/CommSortSelect";

interface CommEditInitial {
    body?: string;
    tagIds?: number[];
    thumbId?: number;
    thumbUrl?: string;
    contentMedia?: { id: number; file_url: string; file_type: string }[];
}

function exclusiveCreatePost(): void {
    Logger.info("CommController.exclusiveCreatePost");

    const form = document.getElementById(
        "comm-create-form",
    ) as HTMLFormElement | null;
    if (!form) return;

    let initial: CommEditInitial | undefined;
    const payloadJson = form.getAttribute("data-edit-payload");
    if (payloadJson) {
        try {
            initial = JSON.parse(payloadJson) as CommEditInitial;
        } catch {
            initial = undefined;
        }
    }

    const csrf =
        (
            document.querySelector(
                'input[name="_token"]',
            ) as HTMLInputElement | null
        )?.value ?? "";
    const contentModeInput = document.getElementById(
        "create-post-mode",
    ) as HTMLInputElement | null;
    const bodyInput = document.getElementById(
        "create-body-input",
    ) as HTMLInputElement | null;
    const tabs = form.querySelectorAll(".comm-create__tab");
    const panels = form.querySelectorAll(".comm-create__panel");
    const linkUrlEl = document.getElementById(
        "create-link-url",
    ) as HTMLInputElement | null;
    const linkTitleEl = document.getElementById(
        "create-link-title",
    ) as HTMLInputElement | null;
    const editorEl = document.getElementById("create-content-editor");

    const uploadUrl = form.dataset.uploadUrl ?? "";

    let quillEditor: QuillEditor | null = null;
    if (editorEl && uploadUrl && csrf) {
        const placeholder = editorEl.getAttribute("data-placeholder") ?? "";
        quillEditor = new QuillEditor(editorEl, {
            placeholder,
            upload: {
                uploadUrl,
                csrf,
                fileKey: "file",
            },
        });
        quillEditor.init();
        if (initial?.body !== undefined && initial.body !== "" && quillEditor) {
            quillEditor.setHtml(initial.body);
        }
    }

    const titleInput = document.getElementById(
        "create-title",
    ) as HTMLInputElement | null;
    const showPanel = (tab: Element): void => {
        const mode = tab.getAttribute("data-tab") ?? "text";
        if (contentModeInput) contentModeInput.value = mode;
        tabs.forEach((t) => {
            t.classList.toggle("is-active", t === tab);
            t.setAttribute("aria-selected", t === tab ? "true" : "false");
        });
        panels.forEach((p) => {
            const show = p.getAttribute("data-panel") === mode;
            p.classList.toggle("is-active", show);
            (p as HTMLElement).hidden = !show;
        });
        if (titleInput) titleInput.required = mode === "text";
    };
    tabs.forEach((tab) => tab.addEventListener("click", () => showPanel(tab)));

    form.addEventListener(
        "submit",
        (e) => {
            const mode = contentModeInput?.value ?? "text";
            if (mode === "text" && quillEditor && bodyInput) {
                bodyInput.value = stripBase64Images(quillEditor.getHtml());
            } else if (mode === "link" && bodyInput) {
                const url = linkUrlEl?.value?.trim() ?? "";
                const title = (linkTitleEl?.value?.trim() ?? url) || url;
                if (!url) {
                    e.preventDefault();
                    alert("링크 URL을 입력하세요.");
                    return;
                }
                bodyInput.value =
                    '<p><a href="' +
                    url.replace(/"/g, "&quot;") +
                    '">' +
                    (title || url).replace(/</g, "&lt;").replace(/>/g, "&gt;") +
                    "</a></p>";
            } else if (bodyInput) {
                bodyInput.value = "";
            }
        },
        true,
    );

    const thumbDrop = document.getElementById("comm-thumb-dropzone");
    const thumbInput = document.getElementById(
        "comm-thumb-input",
    ) as HTMLInputElement | null;
    const thumbIdInput = document.getElementById(
        "comm-thumb-id",
    ) as HTMLInputElement | null;
    const thumbPreview = document.getElementById("comm-thumb-preview");
    const thumbUploadUrl = document
        .getElementById("comm-thumb-upload")
        ?.getAttribute("data-upload-url");

    if (thumbUploadUrl && thumbInput && thumbIdInput) {
        const uploadThumb = (file: File): void => {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("_token", csrf);
            fd.append("category", "image");
            fd.append("target_type", "post");
            fd.append("target_id", "0");
            fetch(thumbUploadUrl, {
                method: "POST",
                body: fd,
                headers: {
                    "X-Requested-With": "XMLHttpRequest",
                    Accept: "application/json",
                },
            })
                .then((r) => r.json())
                .then((json: Record<string, unknown>) => {
                    const data = json?.data as
                        | Record<string, unknown>
                        | undefined;
                    if (json?.result === "success" && data?.id) {
                        thumbIdInput.value = String(data.id);
                        if (thumbPreview && data?.file_url) {
                            thumbPreview.innerHTML =
                                '<img src="' +
                                (data.file_url as string) +
                                '" alt="" style="max-width:200px;height:auto;border-radius:4px;">';
                        }
                    } else {
                        alert((json?.msg as string) || "업로드 실패");
                    }
                })
                .catch(() => alert("업로드 중 오류"));
        };
        thumbDrop?.addEventListener("click", () => thumbInput.click());
        thumbInput.addEventListener("change", () => {
            const file = thumbInput.files?.[0];
            thumbInput.value = "";
            if (file?.type?.startsWith("image/")) uploadThumb(file);
        });
    }

    const mediaList = document.getElementById("comm-media-list");
    const mediaInput = document.getElementById(
        "comm-media-input",
    ) as HTMLInputElement | null;
    const mediaDropzone = document.getElementById("comm-media-dropzone");
    const mediaUploadWrap = document.getElementById("comm-media-upload");
    const mediaUploadUrl = mediaUploadWrap?.getAttribute("data-upload-url");
    const maxMedia =
        parseInt(mediaUploadWrap?.getAttribute("data-max") ?? "20", 10) || 20;
    const mediaIds: number[] = [];

    if (mediaUploadUrl && mediaList && mediaInput) {
        const addMediaItem = (
            id: number,
            fileUrl: string,
            fileType: string,
        ): void => {
            mediaIds.push(id);
            const wrap = document.createElement("div");
            wrap.className = "comm-create__media-item";
            wrap.style.cssText =
                "display:inline-block;margin:4px;vertical-align:top;";
            if ((fileType || "").indexOf("image") !== -1) {
                const img = document.createElement("img");
                img.src = fileUrl;
                img.alt = "";
                img.style.cssText =
                    "width:80px;height:80px;object-fit:cover;border-radius:4px;";
                wrap.appendChild(img);
            } else {
                const vid = document.createElement("video");
                vid.src = fileUrl;
                vid.controls = true;
                vid.style.cssText =
                    "width:120px;height:80px;border-radius:4px;";
                wrap.appendChild(vid);
            }
            const hid = document.createElement("input");
            hid.type = "hidden";
            hid.name = "content_media_ids[]";
            hid.value = String(id);
            wrap.appendChild(hid);
            const btn = document.createElement("button");
            btn.type = "button";
            btn.textContent = "×";
            btn.style.cssText = "margin-left:4px;cursor:pointer;";
            btn.addEventListener("click", () => {
                const idx = mediaIds.indexOf(id);
                if (idx > -1) mediaIds.splice(idx, 1);
                wrap.remove();
            });
            wrap.appendChild(btn);
            mediaList.appendChild(wrap);
        };

        const uploadMedia = (file: File): void => {
            if (mediaIds.length >= maxMedia) {
                alert(`최대 ${maxMedia}개까지 업로드할 수 있습니다.`);
                return;
            }
            const fd = new FormData();
            fd.append("file", file);
            fd.append("_token", csrf);
            fd.append(
                "category",
                file.type?.startsWith("video/") ? "video" : "image",
            );
            fd.append("target_type", "post");
            fd.append("target_id", "0");
            fd.append("meta_role", "content_media");
            fetch(mediaUploadUrl, {
                method: "POST",
                body: fd,
                headers: {
                    "X-Requested-With": "XMLHttpRequest",
                    Accept: "application/json",
                },
            })
                .then((r) => r.json())
                .then((json: Record<string, unknown>) => {
                    const data = json?.data as
                        | Record<string, unknown>
                        | undefined;
                    if (json?.result === "success" && data?.id) {
                        addMediaItem(
                            data.id as number,
                            (data.file_url as string) ?? "",
                            (data.file_type as string) ?? "",
                        );
                    } else {
                        alert((json?.msg as string) || "업로드 실패");
                    }
                })
                .catch(() => alert("업로드 중 오류"));
        };

        mediaDropzone?.addEventListener("click", () => mediaInput.click());
        mediaInput.addEventListener("change", () => {
            const files = mediaInput.files;
            mediaInput.value = "";
            if (files)
                for (let i = 0; i < files.length; i++) uploadMedia(files[i]);
        });

        if (initial?.contentMedia?.length) {
            initial.contentMedia.forEach((m) =>
                addMediaItem(m.id, m.file_url || "", m.file_type || "image"),
            );
        }
    }
}

/**
 * 게시글 상세: 리액션 토글 + 댓글 영역(옵션 메뉴, 반응 픽커, 답글 토글, 모달)
 */
function postDetail(): void {
    Logger.info("CommController.postDetail");

    const heroSwiperEl = document.querySelector("[data-post-hero-swiper]");
    if (
        heroSwiperEl &&
        typeof (window as Window & { Swiper?: unknown }).Swiper !== "undefined"
    ) {
        const SwiperClass = (
            window as Window & {
                Swiper: new (el: Element, o: object) => unknown;
            }
        ).Swiper;
        const slides = heroSwiperEl.querySelectorAll(".swiper-slide");
        const paginationEl = heroSwiperEl.querySelector(
            ".comm-detail__hero-pagination",
        );
        const usePagination =
            slides.length > 1 && paginationEl instanceof HTMLElement;
        new SwiperClass(heroSwiperEl, {
            slidesPerView: 1,
            spaceBetween: 0,
            loop: slides.length > 1,
            pagination: usePagination
                ? { el: paginationEl, clickable: true }
                : false,
        });
    }

    const reactionsEl = document.querySelector("[data-post-reactions]");
    if (reactionsEl) {
        const url = reactionsEl.getAttribute("data-reaction-url");
        const csrf = reactionsEl.getAttribute("data-csrf");
        const picker = reactionsEl.querySelector("[data-reaction-picker]");

        const applyReactionResponse = (data: {
            reaction_count?: number;
            reaction_counts_by_type?: Record<string, number>;
            current_user_reaction_type_ids?: number[];
        }) => {
            const activeIds = (data.current_user_reaction_type_ids ?? []).map(
                String,
            );
            const counts = data.reaction_counts_by_type ?? {};
            const pills = reactionsEl.querySelectorAll(
                ".comm-detail__reaction-pill",
            );
            pills.forEach((b) => {
                const id = b.getAttribute("data-reaction-type-id");
                if (!id) return;
                const cnt = Number(counts[id]) || 0;
                b.classList.toggle("is-active", activeIds.includes(id));
                b.classList.toggle("is-hidden", cnt < 1);
                b.setAttribute(
                    "aria-pressed",
                    b.classList.contains("is-active") ? "true" : "false",
                );
                b.setAttribute("aria-hidden", cnt < 1 ? "true" : "false");
                const countEl = b.querySelector(
                    `.comm-detail__reaction-count[data-count-for="${id}"]`,
                );
                if (countEl) countEl.textContent = String(cnt);
            });
            const label = reactionsEl.querySelector(
                ".comm-detail__reactions-label",
            );
            if (label) {
                const fmt = label.getAttribute("data-format") || "공감 %count%";
                const count =
                    data.reaction_count != null
                        ? Number(data.reaction_count).toLocaleString()
                        : "0";
                label.textContent = fmt.replace("%count%", count);
            }
        };

        const sendReaction = (typeId: string, btn: HTMLElement) => {
            (btn as HTMLButtonElement).disabled = true;
            const body = new FormData();
            body.append("reaction_type_id", typeId);
            if (csrf) body.append("_token", csrf);
            fetch(url!, {
                method: "POST",
                body,
                headers: { "X-Requested-With": "XMLHttpRequest" },
            })
                .then((r) => r.json())
                .then(
                    (data: {
                        success?: boolean;
                        current_user_reaction_type_ids?: number[];
                        reaction_count?: number;
                        reaction_counts_by_type?: Record<string, number>;
                    }) => {
                        (btn as HTMLButtonElement).disabled = false;
                        if (data.success) {
                            applyReactionResponse(data);
                            picker?.classList.add("is-hidden");
                        }
                    },
                )
                .catch(() => {
                    (btn as HTMLButtonElement).disabled = false;
                });
        };

        reactionsEl.addEventListener("click", (e) => {
            const pill = (e.target as HTMLElement).closest(
                ".comm-detail__reaction-pill",
            );
            if (pill && !pill.classList.contains("is-hidden")) {
                const typeId = pill.getAttribute("data-reaction-type-id");
                if (typeId && url) {
                    e.preventDefault();
                    sendReaction(typeId, pill as HTMLElement);
                }
                return;
            }
            const addBtn = (e.target as HTMLElement).closest(
                ".comm-detail__reaction-add",
            );
            if (addBtn) {
                e.preventDefault();
                e.stopPropagation();
                picker?.classList.toggle("is-hidden");
                return;
            }
            const pickerBtn = (e.target as HTMLElement).closest(
                ".comm-detail__reaction-picker-btn",
            );
            if (pickerBtn) {
                e.preventDefault();
                const typeId = pickerBtn.getAttribute("data-reaction-type-id");
                if (typeId && url)
                    sendReaction(typeId, pickerBtn as HTMLElement);
                return;
            }
        });

        document.addEventListener("click", (ev) => {
            if (
                picker &&
                !picker.classList.contains("is-hidden") &&
                !reactionsEl.contains(ev.target as Node)
            ) {
                picker.classList.add("is-hidden");
            }
        });
    }

    const section = document.querySelector("[data-comments-section]");
    if (section) {
        initCommentProvider(section);
    }
}

/**
 * Exclusive contents: Pinned Swiper는 runGlobalInits에서 PinPostSwiper로 이미 초기화됨.
 * 액션이 필요하면 여기서 추가 초기화.
 */
function exclusiveExclusiveContents(): void {
    Logger.info("CommController.exclusiveExclusiveContents");
}

function language(): void {
    Logger.info("CommController.language");
    new CountrySelect("#comm-language-country").init();

    const langForm = document.getElementById(
        "comm-language-form",
    ) as HTMLFormElement | null;
    if (langForm) {
        new Form(langForm, {
            loaderSelector: ".profile-page__btn-loader",
            onSuccess: (data) => {
                const redirect = (
                    data?.data as { redirect?: string } | undefined
                )?.redirect;
                if (redirect) {
                    window.location.href = redirect;
                }
            },
        }).init();
    }
}

/** 프로필 페이지: 회원 탈퇴 폼 (Form.ts + Modal.confirm) */
function profile(): void {
    Logger.info("CommController.profile");
    const form = document.getElementById(
        "profile-delete-account-form",
    ) as HTMLFormElement | null;
    if (!form) return;

    const submitUrl = form.dataset.submitUrl ?? form.action;
    const confirmMessage =
        form.dataset.confirmMessage ?? "정말 탈퇴하시겠습니까?";
    const confirmTitle = document.documentElement.lang?.startsWith("ko")
        ? "회원 탈퇴"
        : "Delete Account";

    new Form(form, {
        submitUrl,
        validate: async () => {
            const ok = await Modal.confirm(confirmMessage, confirmTitle);
            return ok ? null : "CANCEL";
        },
        requestFailedMessage: document.documentElement.lang?.startsWith("ko")
            ? "요청에 실패했습니다."
            : "Request failed.",
        onFail: (message) => {
            if (message !== "CANCEL") {
                Modal.alert(message);
            }
        },
        onSuccess: async (data) => {
            const msg = (data?.message ?? data?.msg ?? "") as string;
            const redirect = (data?.data as { redirect?: string } | undefined)
                ?.redirect;
            if (msg) await Modal.alert(msg);
            if (redirect) window.location.href = redirect;
        },
    }).init();
}

function myPosts(): void {
    Logger.info("CommController.myPosts");
    new CommSortSelect("#sort-posts").init();
    new CommSortSelect("#board-posts").init();

    const page = document.querySelector(".profile-page");
    const container = document.querySelector<HTMLElement>(
        "[data-my-posts-list]",
    );
    const apiUrl = page?.getAttribute("data-api-my-posts");
    if (container && apiUrl) {
        const form = document.querySelector<HTMLFormElement>(
            ".profile-page__filters",
        );
        const list = new MyPostList({ container, fetchUrl: apiUrl, form });
        list.load();
        form?.addEventListener("submit", (e) => {
            e.preventDefault();
            list.load();
        });
    }
}

function myComments(): void {
    Logger.info("CommController.myComments");
    new CommSortSelect("#sort-comments").init();

    const page = document.querySelector(".profile-page");
    const container = document.querySelector<HTMLElement>(
        "[data-my-comments-list]",
    );
    const apiUrl = page?.getAttribute("data-api-my-comments");
    if (container && apiUrl) {
        const form = document.querySelector<HTMLFormElement>(
            ".profile-page__filters",
        );
        const list = new MyCommentList({ container, fetchUrl: apiUrl, form });
        list.load();
        form?.addEventListener("submit", (e) => {
            e.preventDefault();
            list.load();
        });
    }
}

function blockedMembers(): void {
    Logger.info("CommController.blockedMembers");
    const confirmTitle = document.documentElement.lang?.startsWith("ko")
        ? "확인"
        : "Confirm";
    document
        .querySelectorAll<HTMLFormElement>("form[data-confirm]")
        .forEach((form) => {
            new Form(form, {
                validate: async () => {
                    const msg =
                        form.getAttribute("data-confirm") ??
                        "진행하시겠습니까?";
                    const ok = await Modal.confirm(msg, confirmTitle);
                    return ok ? null : "CANCEL";
                },
                onFail: (message) => {
                    if (message !== "CANCEL") Modal.alert(message);
                },
            }).init();
        });
}

function myReports(): void {
    Logger.info("CommController.myReports");
    const page = document.querySelector(
        ".profile-page[data-api-report-withdraw]",
    );
    if (!page) return;
    const withdrawUrl = page.getAttribute("data-api-report-withdraw") ?? "";
    if (!withdrawUrl) return;
    const csrf =
        (
            document.querySelector(
                'meta[name="csrf-token"]',
            ) as HTMLMetaElement | null
        )?.content ??
        (
            document.querySelector(
                'input[name="_token"]',
            ) as HTMLInputElement | null
        )?.value ??
        "";
    document
        .querySelectorAll<HTMLElement>("[data-report-withdraw]")
        .forEach((btn) => {
            btn.addEventListener("click", async () => {
                const reportId = btn.getAttribute("data-report-id") ?? "";
                if (!reportId) return;
                const body = new FormData();
                body.append("report_id", reportId);
                if (csrf) body.append("_token", csrf);
                let message = "";
                try {
                    const res = await fetch(withdrawUrl, {
                        method: "POST",
                        body,
                        headers: {
                            Accept: "application/json",
                            "X-Requested-With": "XMLHttpRequest",
                        },
                    });
                    let data: { success?: boolean; message?: string } = {};
                    try {
                        data = (await res.json()) as {
                            success?: boolean;
                            message?: string;
                        };
                    } catch {
                        message = "응답을 확인할 수 없습니다.";
                    }
                    message =
                        message ||
                        (data.message ??
                            (data.success
                                ? "신고가 철회되었습니다."
                                : "처리에 실패했습니다."));
                    if (data.success) {
                        const li = btn.closest(
                            "li.profile-page__list-item--report",
                        );
                        if (li) li.remove();
                    }
                } catch {
                    message = "요청에 실패했습니다.";
                }
                await Modal.alert(message);
            });
        });
}

/** Edit post page: same form as create with prefill from window.__COMM_EDIT_INITIAL__ */
function exclusiveEditPost(): void {
    Logger.info("CommController.exclusiveEditPost");
    exclusiveCreatePost();
}

export const CommController = {
    exclusiveCreatePost,
    exclusiveEditPost,
    createPostBySlug: exclusiveCreatePost,
    editPostBySlug: exclusiveEditPost,
    postDetail,
    exclusiveExclusiveContents,
    language,
    profile,
    myPosts,
    myComments,
    blockedMembers,
    myReports,
};
